export const MAX_FILE_MB = 100
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

const SUPPORTED_INFO_KEYS = new Set([
  'Title',
  'Author',
  'Subject',
  'Keywords',
  'CreationDate',
  'ModDate',
  'Creator',
  'Producer',
])

export interface PdfMetadataFields {
  title: string
  author: string
  subject: string
  keywords: string
  creationDate: string
  modificationDate: string
}

export interface PdfMetadataSnapshot {
  fields: PdfMetadataFields
  totalPages: number
  /** Read-only: the application that created the source document. */
  creator: string
  /** Read-only: the application that wrote the PDF. */
  producer: string
  unsupportedInfoKeys: string[]
  hasXmpMetadata: boolean
}

export interface PdfMetadataSaveResult extends PdfMetadataSnapshot {
  blob: Blob
}

interface PdfNameFactory {
  of: (name: string) => unknown
}

interface PdfDictionaryLike {
  delete: (key: unknown) => void
  keys?: () => unknown[]
}

interface PdfDocumentLike {
  catalog?: {
    get?: (...args: any[]) => unknown
  }
  context?: {
    trailerInfo?: {
      Info?: unknown
    }
    lookup?: (...args: any[]) => unknown
  }
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function metadataFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-metadata.pdf`
}

export async function readPdfMetadata(file: File): Promise<PdfMetadataSnapshot> {
  const { PDFDocument, PDFName } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes, { updateMetadata: false })

  return snapshotPdfMetadata(pdfDoc, PDFName)
}

export async function savePdfMetadata(
  file: File,
  fields: PdfMetadataFields,
): Promise<PdfMetadataSaveResult> {
  const { PDFDocument, PDFName } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes, { updateMetadata: false })

  applyTextMetadata(pdfDoc, PDFName, 'Title', fields.title, (value) => pdfDoc.setTitle(value))
  applyTextMetadata(pdfDoc, PDFName, 'Author', fields.author, (value) => pdfDoc.setAuthor(value))
  applyTextMetadata(pdfDoc, PDFName, 'Subject', fields.subject, (value) => pdfDoc.setSubject(value))
  applyKeywords(pdfDoc, PDFName, fields.keywords)
  applyDateMetadata(pdfDoc, PDFName, 'CreationDate', fields.creationDate, (value) =>
    pdfDoc.setCreationDate(value),
  )
  applyDateMetadata(pdfDoc, PDFName, 'ModDate', fields.modificationDate, (value) =>
    pdfDoc.setModificationDate(value),
  )

  const out = await pdfDoc.save()
  return {
    ...snapshotPdfMetadata(pdfDoc, PDFName),
    blob: new Blob([out], { type: 'application/pdf' }),
  }
}

function snapshotPdfMetadata(
  pdfDoc: {
    getTitle: () => string | undefined
    getAuthor: () => string | undefined
    getSubject: () => string | undefined
    getKeywords: () => string[] | string | undefined
    getCreationDate: () => Date | undefined
    getModificationDate: () => Date | undefined
    getCreator: () => string | undefined
    getProducer: () => string | undefined
    getPageCount: () => number
  } & PdfDocumentLike,
  PDFName: PdfNameFactory,
): PdfMetadataSnapshot {
  return {
    fields: {
      title: pdfDoc.getTitle() ?? '',
      author: pdfDoc.getAuthor() ?? '',
      subject: pdfDoc.getSubject() ?? '',
      keywords: normalizeKeywords(pdfDoc.getKeywords()),
      creationDate: toDatetimeLocalValue(pdfDoc.getCreationDate()),
      modificationDate: toDatetimeLocalValue(pdfDoc.getModificationDate()),
    },
    totalPages: pdfDoc.getPageCount(),
    creator: pdfDoc.getCreator() ?? '',
    producer: pdfDoc.getProducer() ?? '',
    unsupportedInfoKeys: getUnsupportedInfoKeys(pdfDoc),
    hasXmpMetadata: hasXmpMetadata(pdfDoc, PDFName),
  }
}

function normalizeKeywords(value: string[] | string | undefined): string {
  if (!value) return ''
  if (Array.isArray(value)) return value.join(', ')
  return value
}

function toDatetimeLocalValue(value: Date | undefined): string {
  if (!value || Number.isNaN(value.getTime())) return ''

  const offsetMs = value.getTimezoneOffset() * 60 * 1000
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16)
}

function parseDatetimeLocalValue(value: string): Date | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) {
    throw new Error('invalid_date')
  }
  return date
}

function applyTextMetadata(
  pdfDoc: PdfDocumentLike,
  PDFName: PdfNameFactory,
  key: string,
  value: string,
  setter: (value: string) => void,
) {
  deleteInfoKey(pdfDoc, PDFName, key)
  const trimmed = value.trim()
  if (trimmed) setter(trimmed)
}

function applyKeywords(pdfDoc: PdfDocumentLike, PDFName: PdfNameFactory, value: string) {
  deleteInfoKey(pdfDoc, PDFName, 'Keywords')
  const keywords = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

  if (keywords.length > 0 && 'setKeywords' in pdfDoc && typeof pdfDoc.setKeywords === 'function') {
    pdfDoc.setKeywords(keywords)
  }
}

function applyDateMetadata(
  pdfDoc: PdfDocumentLike,
  PDFName: PdfNameFactory,
  key: string,
  value: string,
  setter: (value: Date) => void,
) {
  deleteInfoKey(pdfDoc, PDFName, key)
  const date = parseDatetimeLocalValue(value)
  if (date) setter(date)
}

function getInfoDictionary(pdfDoc: PdfDocumentLike): PdfDictionaryLike | null {
  const infoRef = pdfDoc.context?.trailerInfo?.Info
  if (!infoRef) return null

  const info = pdfDoc.context?.lookup?.(infoRef)
  if (!info || typeof info !== 'object' || !('delete' in info)) return null

  const dictionary = info as Partial<PdfDictionaryLike>
  return typeof dictionary.delete === 'function' ? (dictionary as PdfDictionaryLike) : null
}

function deleteInfoKey(pdfDoc: PdfDocumentLike, PDFName: PdfNameFactory, key: string) {
  getInfoDictionary(pdfDoc)?.delete(PDFName.of(key))
}

function getUnsupportedInfoKeys(pdfDoc: PdfDocumentLike): string[] {
  const info = getInfoDictionary(pdfDoc)
  const keys = info?.keys?.() ?? []

  return keys
    .map(pdfNameToPlainText)
    .filter((key) => key.length > 0 && !SUPPORTED_INFO_KEYS.has(key))
    .sort((a, b) => a.localeCompare(b))
}

function hasXmpMetadata(pdfDoc: PdfDocumentLike, PDFName: PdfNameFactory): boolean {
  return Boolean(pdfDoc.catalog?.get?.(PDFName.of('Metadata')))
}

function pdfNameToPlainText(value: unknown): string {
  const maybeNamed = value as {
    decodeText?: () => string
    asString?: () => string
    toString?: () => string
  }

  const raw = maybeNamed.decodeText?.() ?? maybeNamed.asString?.() ?? maybeNamed.toString?.() ?? ''
  return raw.startsWith('/') ? raw.slice(1) : raw
}
